import axios from "axios";
import Links from "../constants/Links";

/**
 * Admin operations API hook — operational stats and the system-wide
 * session kill switch.
 */
const Admin = {
    /**
     * @description Operational dashboard stats (sessions, entities, runtime).
     * Auth: ADMIN or SUPER_ADMIN.
     * @returns {Promise<AxiosResponse<*>>}
     */
    systemStats: async () => {
        return axios.get(Links.admin.systemStats)
    },

    /**
     * @description System-wide kill switch: revokes every active session
     * except those owned by SUPER_ADMIN users. Auth: SUPER_ADMIN only.
     * @returns {Promise<AxiosResponse<*>>} body: { revoked: number }
     */
    revokeAllSessions: async () => {
        return axios.post(Links.admin.revokeAllSessions)
    },

    /**
     * @description Create a new admin user. Backend assigns default password
     * "12345678" and the ADMIN role. Auth: SUPER_ADMIN only.
     * @param {{userId:string, firstName:string, lastName:string, email:string, phone:string}} payload
     * @returns {Promise<AxiosResponse<*>>}
     */
    create: async (payload) => {
        return axios.post(Links.admin.create, payload)
    },

    /**
     * @description Update an admin's profile fields. Does not change password,
     * role, or admin flags. Auth: SUPER_ADMIN only.
     * @param {number|string} id database id of the admin to update
     * @param {{userId:string, firstName:string, lastName:string, email:string, phone:string}} payload
     * @returns {Promise<AxiosResponse<*>>}
     */
    update: async (id, payload) => {
        return axios.put(`${Links.admin.update}${id}`, payload)
    },

    /**
     * @description Paginated list of admins (users with ADMIN role).
     * Auth: SUPER_ADMIN only.
     * @param {number} page zero-indexed page number
     * @param {number} size items per page
     * @returns {Promise<AxiosResponse<*>>} Spring Page<User> envelope
     */
    listPaginated: async (page, size) => {
        return axios.get(Links.admin.list, { params: { page, size } })
    },

    /**
     * @description Block an admin: sets locked=true and revokes all their
     * active sessions. Auth: SUPER_ADMIN only. Cannot target self or another
     * SUPER_ADMIN.
     * @param {number|string} id database id of the admin to block
     * @returns {Promise<AxiosResponse<*>>} body: { id, userId, email, locked, sessionsRevoked }
     */
    block: async (id) => {
        return axios.post(`${Links.admin.update}${id}/block`)
    },

    /**
     * @description Unblock an admin: sets locked=false. Auth: SUPER_ADMIN only.
     * @param {number|string} id database id of the admin to unblock
     * @returns {Promise<AxiosResponse<*>>} body: { id, userId, email, locked, sessionsRevoked }
     */
    unblock: async (id) => {
        return axios.post(`${Links.admin.update}${id}/unblock`)
    },
}

export default Admin
